import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, Button, Grid, Chip,
  CircularProgress, Alert, Switch, Stack, Divider, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Accordion,
  AccordionSummary, AccordionDetails, useTheme, useMediaQuery,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  Shield as ShieldIcon,
  FlashOn as FlashIcon,
  Groups as GroupsIcon,
  Business as BusinessIcon,
  WorkspacePremium as PremiumIcon,
  Favorite as HeartIcon,
  Lock as LockIcon,
  CreditCard as CardIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../SEO/SEOHead';

// ─── Plan definitions ───────────────────────────────────────────────────────

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    emoji: '🌱',
    monthlyPrice: 0,
    yearlyPrice: 0,
    color: '#6b7280',
    gradient: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
    description: 'Start your financial journey — no card needed',
    features: [
      { text: '50 transactions / month', included: true },
      { text: '5 budget categories', included: true },
      { text: '3 receipt uploads / month', included: true },
      { text: 'Basic expense tracking', included: true },
      { text: 'Basic reports', included: true },
      { text: 'Community support', included: true },
      { text: 'AI Insights', included: false },
      { text: 'Data export (CSV)', included: false },
      { text: 'Split bills', included: false },
    ],
  },
  {
    id: 'standard',
    name: 'Standard',
    emoji: '⚡',
    monthlyPrice: 2.99,
    yearlyPrice: 28.70,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
    description: 'For individuals serious about their money',
    features: [
      { text: '200 transactions / month', included: true },
      { text: '15 budget categories', included: true },
      { text: '50 receipt uploads / month', included: true },
      { text: 'Advanced expense tracking', included: true },
      { text: 'Custom reports', included: true },
      { text: 'Data export (CSV)', included: true },
      { text: '10 split bills / month', included: true },
      { text: 'Email support', included: true },
      { text: 'Savings goals', included: true },
      { text: 'Debt payoff tracker', included: true },
      { text: 'Automation rules', included: true },
      { text: 'Smart receipt categorization', included: true },
      { text: 'Receipt re-analysis', included: true },
      { text: 'AI Insights', included: false },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    emoji: '✨',
    monthlyPrice: 4.99,
    yearlyPrice: 47.90,
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
    description: 'Unlock the full power of AI financial intelligence',
    badge: 'Most Popular',
    popular: true,
    features: [
      { text: 'Unlimited transactions', included: true },
      { text: 'Unlimited budget categories', included: true },
      { text: '200 receipt uploads / month', included: true },
      { text: 'AI-powered spending insights', included: true },
      { text: 'Advanced analytics & charts', included: true },
      { text: 'Data export (CSV/Excel)', included: true },
      { text: 'Unlimited split bills', included: true },
      { text: 'Priority support', included: true },
      { text: 'Bill payment reminders', included: true },
      { text: 'Cashflow forecasting', included: true },
      { text: 'Spending anomaly alerts', included: true },
      { text: 'Branded PDF reports', included: true },
      { text: 'AI receipt insights', included: true },
    ],
  },
  {
    id: 'family',
    name: 'Family',
    emoji: '🏡',
    monthlyPrice: 9.99,
    yearlyPrice: 95.90,
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #059669 0%, #34d399 100%)',
    description: 'Everything Premium, shared with your household',
    features: [
      { text: 'Everything in Premium', included: true },
      { text: 'Up to 5 user accounts', included: true },
      { text: '500 receipt uploads / month', included: true },
      { text: 'Shared budget categories', included: true },
      { text: 'Role-based permissions', included: true },
      { text: 'Household spending analytics', included: true },
      { text: 'Financial goal collaboration', included: true },
      { text: 'Annual financial review', included: true },
      { text: 'Family insights dashboard', included: true },
    ],
  },
  {
    id: 'business',
    name: 'Business',
    emoji: '🏢',
    monthlyPrice: 24.99,
    yearlyPrice: 239.90,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #d97706 0%, #fbbf24 100%)',
    description: 'Enterprise-grade for teams & businesses',
    features: [
      { text: 'Everything in Family', included: true },
      { text: 'Up to 15 user accounts', included: true },
      { text: 'Unlimited receipts', included: true },
      { text: 'Advanced analytics dashboard', included: true },
      { text: 'Custom financial reports', included: true },
      { text: 'API access', included: true },
      { text: 'Dedicated account manager', included: true },
      { text: 'White-label options', included: true },
      { text: '24/7 priority support', included: true },
    ],
  },
];

const FAQ = [
  { q: 'Can I change my plan at any time?', a: 'Yes — upgrades take effect immediately. Downgrades apply at your next billing date. No lock-ins.' },
  { q: 'Is there a free trial?', a: 'Every paid plan includes a 14-day free trial. No credit card required to start.' },
  { q: 'What payment methods are accepted?', a: 'All major credit/debit cards and bank transfers via Stripe, the industry-standard secure payment processor.' },
  { q: 'How does yearly billing work?', a: 'You pay once a year and save ~20% compared to monthly billing. The discount is applied automatically at checkout.' },
  { q: 'What happens when I cancel?', a: 'Cancellation is immediate and your account reverts to the Free plan. Your data is never deleted.' },
  { q: 'Is my financial data secure?', a: 'Absolutely. We use 256-bit SSL encryption, bank-level security, and never sell your data to anyone.' },
];

// ─── Plan Card ────────────────────────────────────────────────────────────────

const PlanCard = ({ plan, billingCycle, currentPlan, onSelect, subscribing, unavailable }) => {
  const theme = useTheme();
  const isCurrent = currentPlan?.plan === plan.id;
  const isPopular = plan.popular;
  const isDark = theme.palette.mode === 'dark';

  const price = billingCycle === 'yearly'
    ? (plan.yearlyPrice / 12).toFixed(2)
    : plan.monthlyPrice.toFixed(2);

  const buttonLabel = isCurrent ? 'Current Plan' : plan.monthlyPrice === 0 ? 'Get Started Free' : 'Subscribe Now';

  return (
    <Box
      sx={{
        position: 'relative',
        pt: isPopular ? 2 : 0,
        height: '100%',
      }}
    >
      {/* Popular badge */}
      {isPopular && (
        <Box sx={{
          position: 'absolute', top: 0, left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          background: plan.gradient,
          color: '#fff', px: 2.5, py: 0.6, borderRadius: 20,
          fontSize: '0.75rem', fontWeight: 800, letterSpacing: 1,
          whiteSpace: 'nowrap', boxShadow: `0 4px 20px ${plan.color}60`,
          textTransform: 'uppercase',
        }}>
          ⭐ Most Popular
        </Box>
      )}

      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          position: 'relative',
          overflow: 'visible',
          background: isDark
            ? isPopular
              ? 'linear-gradient(145deg, rgba(124,58,237,0.18) 0%, rgba(17,17,34,0.95) 100%)'
              : 'rgba(255,255,255,0.04)'
            : isPopular
              ? 'linear-gradient(145deg, rgba(124,58,237,0.06) 0%, rgba(255,255,255,0.98) 100%)'
              : '#fff',
          backdropFilter: 'blur(20px)',
          border: isPopular
            ? `2px solid ${plan.color}80`
            : isCurrent
              ? '2px solid #10b981'
              : isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
          boxShadow: isPopular
            ? `0 8px 40px ${plan.color}30, 0 2px 8px rgba(0,0,0,0.2)`
            : isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.06)',
          transform: isPopular ? 'scale(1.03)' : 'scale(1)',
          transition: 'transform 0.25s ease, box-shadow 0.25s ease',
          '&:hover': {
            transform: isPopular ? 'scale(1.05)' : 'scale(1.02)',
            boxShadow: `0 12px 48px ${plan.color}40`,
          },
        }}
      >
        {/* Gradient top bar */}
        <Box sx={{
          height: 4, borderRadius: '16px 16px 0 0',
          background: plan.gradient,
        }} />

        {isCurrent && (
          <Chip label="✓ Active" size="small" sx={{
            position: 'absolute', top: 16, right: 16,
            background: '#10b981', color: '#fff', fontWeight: 700, fontSize: '0.7rem',
          }} />
        )}

        <Box sx={{ p: 3, pb: 1 }}>
          {/* Plan name + emoji */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '1.6rem' }}>{plan.emoji}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{plan.name}</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, minHeight: 40 }}>
            {plan.description}
          </Typography>

          {/* Price */}
          <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '2.6rem', lineHeight: 1, color: plan.color }}>
              €{price}
            </Typography>
            {plan.monthlyPrice > 0 && (
              <Typography color="text.secondary" sx={{ mb: 0.4, fontSize: '0.9rem' }}>
                /mo
              </Typography>
            )}
          </Stack>

          {plan.monthlyPrice === 0 && (
            <Typography color="text.secondary" sx={{ fontSize: '0.85rem', mb: 0.5 }}>forever free</Typography>
          )}

          {billingCycle === 'yearly' && plan.monthlyPrice > 0 && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 700 }}>
                €{plan.yearlyPrice}/year
              </Typography>
              <Chip label="20% OFF" size="small" sx={{
                background: '#10b98122', color: '#10b981',
                fontWeight: 800, fontSize: '0.65rem', height: 20,
              }} />
            </Stack>
          )}
        </Box>

        <Divider sx={{ mx: 3, opacity: 0.5 }} />

        {/* Features */}
        <Box sx={{ p: 3, flexGrow: 1 }}>
          <Stack spacing={1.2}>
            {plan.features.map((f, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={1.2}>
                <Box sx={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: f.included ? `${plan.color}22` : 'transparent',
                  border: f.included ? `1.5px solid ${plan.color}60` : '1.5px solid rgba(150,150,150,0.3)',
                }}>
                  {f.included
                    ? <CheckIcon sx={{ fontSize: 13, color: plan.color }} />
                    : <CloseIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                  }
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.82rem',
                    color: f.included ? 'text.primary' : 'text.disabled',
                    textDecoration: f.included ? 'none' : 'line-through',
                    fontWeight: f.included ? 500 : 400,
                  }}
                >
                  {f.text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        {/* CTA Button */}
        <Box sx={{ p: 3, pt: 0 }}>
          <Button
            fullWidth
            variant={isPopular || isCurrent ? 'contained' : 'outlined'}
            size="large"
            disabled={isCurrent || unavailable || subscribing}
            onClick={() => onSelect(plan)}
            sx={{
              borderRadius: 3,
              py: 1.4,
              fontWeight: 700,
              fontSize: '0.95rem',
              letterSpacing: 0.3,
              background: isPopular && !isCurrent
                ? plan.gradient
                : isCurrent
                  ? '#10b981'
                  : undefined,
              borderColor: plan.color,
              color: (isPopular || isCurrent) ? '#fff' : plan.color,
              '&:hover': {
                background: isPopular && !isCurrent ? plan.gradient : undefined,
                opacity: 0.9,
                boxShadow: `0 4px 20px ${plan.color}50`,
              },
              '&.Mui-disabled': {
                background: isCurrent ? '#10b981' : undefined,
                color: isCurrent ? '#fff' : undefined,
                opacity: isCurrent ? 0.75 : 0.4,
              },
            }}
          >
            {subscribing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : buttonLabel}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const PricingPlans = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();
  const { subscription: ctxSub } = useSubscription();
  const navigate = useNavigate();

  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.get('/subscription/current');
        setCurrentSubscription(res.data.data?.subscription || null);
      } catch (err) {
        if (err.response?.status === 503) setUnavailable(true);
        // Fall back to context data
        setCurrentSubscription(ctxSub || null);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (plan) => {
    if (plan.monthlyPrice === 0) return; // free plan — no checkout
    setSelectedPlan(plan);
    setConfirmOpen(true);
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) return;
    try {
      setSubscribing(true);
      setError(null);
      const res = await api.post('/subscription/create-checkout-session', {
        planId: selectedPlan.id,
        billingCycle,
        successUrl: `${window.location.origin}/subscription/success`,
        cancelUrl: `${window.location.origin}/subscription/plans`,
      });
      window.location.href = res.data.data.sessionUrl;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start checkout. Please try again.');
      setConfirmOpen(false);
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isDark ? '#0d0d1a' : '#f8f9ff',
      }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress sx={{ color: '#7c3aed' }} size={48} />
          <Typography color="text.secondary">Loading plans…</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: isDark
        ? 'radial-gradient(ellipse at top, #1a0a2e 0%, #0d0d1a 60%)'
        : 'radial-gradient(ellipse at top, #ede9fe 0%, #f8f9ff 60%)',
      pb: 12,
    }}>
      <SEOHead
        title="Pricing & Plans"
        description="SoldiKeeper offers a generous free tier plus affordable premium plans with AI receipt scanning, advanced analytics, and priority support."
        canonical="/subscription/plans"
        keywords="soldikeeper pricing, budget app pricing, premium finance app, free budget tracker"
      />

      {/* ── Hero Section ── */}
      <Box sx={{
        pt: { xs: 8, md: 12 }, pb: { xs: 6, md: 8 },
        textAlign: 'center', px: 2,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Back to Dashboard */}
        <Button
          onClick={() => navigate('/dashboard')}
          size="small"
          sx={{
            position: 'absolute', top: 16, left: { xs: 16, md: 32 },
            color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary',
            fontWeight: 600, fontSize: '0.8rem',
            '&:hover': { color: '#7c3aed', background: 'rgba(124,58,237,0.08)' },
            borderRadius: 2, px: 1.5,
          }}
          startIcon={<span style={{ fontSize: 16 }}>←</span>}
        >
          Dashboard
        </Button>
        {/* Ambient glow circles */}
        <Box sx={{
          position: 'absolute', top: -100, left: '20%',
          width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', top: -80, right: '15%',
          width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <Chip
          icon={<TrophyIcon sx={{ fontSize: '0.9rem !important' }} />}
          label="Trusted by 10,000+ users worldwide"
          sx={{
            mb: 3, background: 'linear-gradient(135deg, #7c3aed22, #3b82f622)',
            border: '1px solid rgba(124,58,237,0.3)', color: 'text.primary',
            fontWeight: 600, fontSize: '0.8rem',
          }}
        />

        <Typography
          variant="h2"
          sx={{
            fontWeight: 900,
            fontSize: { xs: '2rem', md: '3.2rem' },
            mb: 2, lineHeight: 1.15,
            background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 50%, #10b981 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          The right plan for{'\u00A0'}every wallet
        </Typography>

        <Typography
          variant="h5"
          color="text.secondary"
          sx={{ mb: 5, fontWeight: 400, maxWidth: 540, mx: 'auto', lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.2rem' } }}
        >
          Start free. Upgrade when you're ready. No surprise charges — ever.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto', mb: 4, borderRadius: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {unavailable && (
          <Alert severity="warning" sx={{ maxWidth: 500, mx: 'auto', mb: 4, borderRadius: 3 }}>
            Payment processing temporarily unavailable. Free plan is fully accessible.
          </Alert>
        )}

        {/* ── Billing toggle ── */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
          <Typography
            onClick={() => setBillingCycle('monthly')}
            sx={{
              fontWeight: billingCycle === 'monthly' ? 700 : 500,
              color: billingCycle === 'monthly' ? 'text.primary' : 'text.secondary',
              cursor: 'pointer', transition: 'all 0.2s', fontSize: '0.95rem',
            }}
          >
            Monthly
          </Typography>

          <Box
            onClick={() => setBillingCycle(c => c === 'monthly' ? 'yearly' : 'monthly')}
            sx={{
              width: 52, height: 28, borderRadius: 14, cursor: 'pointer',
              background: billingCycle === 'yearly'
                ? 'linear-gradient(135deg, #7c3aed, #3b82f6)'
                : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
              position: 'relative', transition: 'background 0.3s',
              border: '2px solid transparent',
              boxShadow: billingCycle === 'yearly' ? '0 2px 12px rgba(124,58,237,0.5)' : 'none',
            }}
          >
            <Box sx={{
              position: 'absolute', top: 2,
              left: billingCycle === 'yearly' ? 24 : 2,
              width: 20, height: 20, borderRadius: '50%',
              background: '#fff',
              transition: 'left 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
            }} />
          </Box>

          <Typography
            onClick={() => setBillingCycle('yearly')}
            sx={{
              fontWeight: billingCycle === 'yearly' ? 700 : 500,
              color: billingCycle === 'yearly' ? 'text.primary' : 'text.secondary',
              cursor: 'pointer', transition: 'all 0.2s', fontSize: '0.95rem',
            }}
          >
            Yearly
          </Typography>

          <Chip
            label="Save 20%"
            size="small"
            sx={{
              background: billingCycle === 'yearly'
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              color: billingCycle === 'yearly' ? '#fff' : 'text.secondary',
              fontWeight: 700, fontSize: '0.72rem',
              transition: 'all 0.3s',
            }}
          />
        </Stack>
      </Box>

      {/* ── Plan Cards ── */}
      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
        <Grid container spacing={3} justifyContent="center" alignItems="stretch">
          {PLANS.map((plan) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={plan.id}
              sx={{ display: 'flex' }}
            >
              <Box sx={{ width: '100%' }}>
                <PlanCard
                  plan={plan}
                  billingCycle={billingCycle}
                  currentPlan={currentSubscription}
                  onSelect={handleSelect}
                  subscribing={subscribing}
                  unavailable={unavailable}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Trust Signals ── */}
      <Container maxWidth="lg" sx={{ mt: 10, px: 2 }}>
        <Grid container spacing={3} justifyContent="center">
          {[
            { icon: <ShieldIcon sx={{ fontSize: 28, color: '#10b981' }} />, title: '256-bit SSL', desc: 'Bank-level encryption protects every request' },
            { icon: <LockIcon sx={{ fontSize: 28, color: '#3b82f6' }} />, title: 'Data Privacy', desc: 'Your data is yours — we never sell it' },
            { icon: <CardIcon sx={{ fontSize: 28, color: '#7c3aed' }} />, title: 'Powered by Stripe', desc: 'Industry-standard secure payment processing' },
            { icon: <HeartIcon sx={{ fontSize: 28, color: '#ef4444' }} />, title: 'Cancel Anytime', desc: 'No lock-ins, no hidden fees, ever' },
          ].map((t) => (
            <Grid item xs={6} md={3} key={t.title}>
              <Box sx={{
                textAlign: 'center', p: 3, borderRadius: 4,
                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                height: '100%',
              }}>
                <Box sx={{ mb: 1 }}>{t.icon}</Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>{t.title}</Typography>
                <Typography variant="caption" color="text.secondary">{t.desc}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Feature Highlights ── */}
      <Container maxWidth="lg" sx={{ mt: 10, px: 2 }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" sx={{ mb: 2 }}>
          Why SoldiKeeper?
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 6, maxWidth: 500, mx: 'auto' }}>
          Built for real people who want real control over their finances.
        </Typography>
        <Grid container spacing={4}>
          {[
            {
              icon: <TrendingUpIcon sx={{ fontSize: 36, color: '#7c3aed' }} />,
              title: 'AI-Powered Insights',
              desc: 'Understand your spending patterns instantly with machine-learning analytics that surface what matters.',
            },
            {
              icon: <FlashIcon sx={{ fontSize: 36, color: '#f59e0b' }} />,
              title: 'Lightning Fast',
              desc: 'Add a transaction in 5 seconds. Scan a receipt in 10. Your finances, always up to date.',
            },
            {
              icon: <GroupsIcon sx={{ fontSize: 36, color: '#3b82f6' }} />,
              title: 'Family & Teams',
              desc: 'Share budgets, split expenses, and collaborate on financial goals with everyone who matters.',
            },
            {
              icon: <PremiumIcon sx={{ fontSize: 36, color: '#10b981' }} />,
              title: 'Premium Experience',
              desc: 'A beautifully designed app with dark mode, multilingual support, and mobile-first design.',
            },
          ].map((f) => (
            <Grid item xs={12} sm={6} key={f.title}>
              <Stack direction="row" spacing={2.5} alignItems="flex-start" sx={{
                p: 3, borderRadius: 4,
                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                transition: 'box-shadow 0.25s',
                '&:hover': { boxShadow: '0 8px 32px rgba(124,58,237,0.15)' },
              }}>
                <Box sx={{
                  p: 1.5, borderRadius: 3, flexShrink: 0,
                  background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                }}>
                  {f.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>{f.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{f.desc}</Typography>
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── FAQ ── */}
      <Container maxWidth="md" sx={{ mt: 10, px: 2 }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" sx={{ mb: 6 }}>
          Frequently Asked Questions
        </Typography>
        <Stack spacing={1.5}>
          {FAQ.map((item, i) => (
            <Accordion
              key={i}
              disableGutters
              elevation={0}
              sx={{
                borderRadius: '12px !important',
                background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)',
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 0.5 }}>
                <Typography fontWeight={600} sx={{ fontSize: '0.95rem' }}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{item.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Container>

      {/* ── Bottom CTA ── */}
      <Container maxWidth="sm" sx={{ mt: 10, textAlign: 'center', px: 2 }}>
        <Box sx={{
          p: 5, borderRadius: 5,
          background: 'linear-gradient(135deg, #7c3aed22, #3b82f622)',
          border: '1px solid rgba(124,58,237,0.25)',
        }}>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 1.5 }}>
            Ready to take control?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Join thousands of users who already track smarter with SoldiKeeper.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="/register"
            sx={{
              borderRadius: 3, px: 5, py: 1.5, fontWeight: 700,
              background: 'linear-gradient(135deg, #7c3aed, #3b82f6)',
              boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
              '&:hover': { boxShadow: '0 6px 28px rgba(124,58,237,0.55)', opacity: 0.95 },
            }}
          >
            Start Free — No Credit Card
          </Button>
        </Box>
      </Container>

      {/* ── Confirm Dialog ── */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs" fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 1, background: isDark ? '#1a1a2e' : '#fff' }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.2rem' }}>
          Confirm: {selectedPlan?.name} Plan
        </DialogTitle>
        <DialogContent>
          <Box sx={{
            p: 2, borderRadius: 3, mb: 2,
            background: selectedPlan ? `${selectedPlan.color}15` : 'transparent',
            border: selectedPlan ? `1px solid ${selectedPlan.color}30` : 'none',
          }}>
            <Typography variant="h4" fontWeight={900} sx={{ color: selectedPlan?.color }}>
              €{billingCycle === 'yearly'
                ? ((selectedPlan?.yearlyPrice || 0) / 12).toFixed(2)
                : (selectedPlan?.monthlyPrice || 0).toFixed(2)}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                /month{billingCycle === 'yearly' ? ' (billed yearly)' : ''}
              </Typography>
            </Typography>
            {billingCycle === 'yearly' && selectedPlan && (
              <Chip label="20% yearly discount applied" size="small" sx={{ mt: 1, background: '#10b98122', color: '#10b981', fontWeight: 700 }} />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            You'll be redirected to <strong>Stripe</strong> to complete payment securely.
            You can cancel your subscription at any time from your account settings.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ borderRadius: 2, color: 'text.secondary' }}>
            Go Back
          </Button>
          <Button
            onClick={handleSubscribe}
            variant="contained"
            disabled={subscribing}
            sx={{
              borderRadius: 2, px: 3, fontWeight: 700,
              background: selectedPlan?.gradient,
              '&:hover': { opacity: 0.9 },
            }}
          >
            {subscribing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Continue to Payment →'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingPlans;
